/**
 * service-reach.js — pure helpers that tell "this backend answered no" apart
 * from "this backend is not behind this face at all", and turn a permission
 * probe into a yes/no. No `@/` imports, so `node --test tests/unit/` covers it.
 *
 * Why (2026-10-22): on the site-b face `dev.denvstack.com` backup-api is not
 * deployed, so `/api/backup/*` falls through to the website's own nginx and
 * comes back `404 text/plain "Not Found"`. backup-api itself is FastAPI and
 * answers every 404 — unknown route or missing job — with a JSON body, so a
 * non-JSON 404 can only mean "no backup-api here".
 *
 * Author: Matew Makhatadze
 */

function headerValue(headers, name) {
  if (!headers) return ''
  if (typeof headers.get === 'function') {
    const v = headers.get(name)
    if (v) return String(v)
  }
  const lower = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lower) return String(headers[key] || '')
  }
  return ''
}

/**
 * TRUE when an axios-style error is a 404 that did NOT come from the API we
 * meant to reach: the body is not JSON (the edge/website answered instead).
 * A JSON 404 ("Backup job not found", `{"detail":"Not Found"}`) is the API
 * itself and returns FALSE.
 */
export function isRouteNotServed(err) {
  const resp = err && err.response
  if (!resp || resp.status !== 404) return false
  const ct = headerValue(resp.headers, 'content-type').toLowerCase()
  if (ct) return !ct.includes('json')
  // No content-type: decide on the body. An object is a parsed JSON envelope.
  const body = resp.data
  return !(body && typeof body === 'object' && !(typeof Blob !== 'undefined' && body instanceof Blob))
}

/**
 * Read one answer out of `POST /api/auth/permissions/check-batch`.
 * `data` is the unwrapped `{ results: [{resource, action, allowed}] }`.
 * Returns true / false, or null when the answer is not there (caller decides).
 */
export function batchAllowed(data, resource, action) {
  const results = data && Array.isArray(data.results) ? data.results : null
  if (!results) return null
  const hit = results.find((r) => r && r.resource === resource && r.action === action)
  return hit ? hit.allowed === true : null
}

/**
 * TRUE when a 403 is the permission plane's refusal for `resource`
 * (cs-packages `@require_permission`: `{error: "forbidden", resource, action}`).
 */
export function isPermissionDenied(err, resource) {
  const resp = err && err.response
  if (!resp || resp.status !== 403) return false
  const body = resp.data && typeof resp.data === 'object' ? resp.data : {}
  if (body.error !== 'forbidden') return false
  return !resource || !body.resource || body.resource === resource
}
